import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from '@mui/material';
import {
  LevelNumberField,
  AmountRangeField,
  ApproverNameField,
  EnabledToggleField,
} from '../fieldEditors/writeoffConfig';

interface LevelFormData {
  level: number;
  isEnabled: boolean;
  primaryApprover: string;
  backupApprover: string;
  minAmount: number;
  maxAmount: number;
}

interface AddWriteoffApprovalLevelDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (levelData: LevelFormData) => void;
  editingLevel?: LevelFormData | null;
  nextLevelNumber: number;
}

export const AddWriteoffApprovalLevelDialog: React.FC<AddWriteoffApprovalLevelDialogProps> = ({
  open,
  onClose,
  onSave,
  editingLevel,
  nextLevelNumber,
}) => {
  const [formData, setFormData] = useState<LevelFormData>({
    level: nextLevelNumber,
    isEnabled: true,
    primaryApprover: '',
    backupApprover: '',
    minAmount: 0,
    maxAmount: 0,
  });

  // Reset form when dialog opens/closes or when editing level changes
  useEffect(() => {
    if (editingLevel) {
      setFormData({ ...editingLevel });
    } else {
      setFormData({
        level: nextLevelNumber,
        isEnabled: true,
        primaryApprover: '',
        backupApprover: '',
        minAmount: 0,
        maxAmount: 0,
      });
    }
  }, [editingLevel, nextLevelNumber, open]);

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const isValid = 
    formData.primaryApprover.trim() !== '' &&
    formData.minAmount >= 0 &&
    formData.maxAmount >= formData.minAmount &&
    formData.level >= 1;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingLevel ? 'Edit Writeoff Approval Level' : 'Add Writeoff Approval Level'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <LevelNumberField
            value={formData.level}
            onChange={(value) => setFormData({ ...formData, level: value })}
          />
          
          <EnabledToggleField
            value={formData.isEnabled}
            onChange={(value) => setFormData({ ...formData, isEnabled: value })}
          />

          <AmountRangeField
            minAmount={formData.minAmount}
            maxAmount={formData.maxAmount}
            onMinAmountChange={(value) => setFormData({ ...formData, minAmount: value })}
            onMaxAmountChange={(value) => setFormData({ ...formData, maxAmount: value })}
          />

          <ApproverNameField
            label="Primary Approver Name"
            value={formData.primaryApprover}
            onChange={(value) => setFormData({ ...formData, primaryApprover: value })}
            required
          />

          <ApproverNameField
            label="Backup Approver Name"
            value={formData.backupApprover}
            onChange={(value) => setFormData({ ...formData, backupApprover: value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid}
          sx={{ textTransform: 'none' }}
        >
          {editingLevel ? 'Update Level' : 'Add Level'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
